import React, { useState, useRef, useEffect } from 'react';
import { ReferenceLapsResponse, ReferenceLap } from '../../models/responseBodies/ReferenceLapsResponse';
import { DriverReferenceSearchResult } from '../../models/responseBodies/DriverReferenceSearchResult';
import { referenceLapFromSearch } from '../../utils/referenceLapFromSearch';
import { formatMilliseconds } from '../../utils/time';

type SearchStatus = 'idle' | 'tooShort' | 'searching' | 'empty' | 'error' | 'ready';

interface ReferenceSelectorProps {
  referenceLaps: ReferenceLapsResponse | null;
  selectedRefType: 'lapRecord' | 'personalBest' | 'driver' | null;
  driverRef: ReferenceLap | null;
  onSelect: (refType: 'lapRecord' | 'personalBest') => void;
  onDriverSelect: (ref: ReferenceLap) => void;
  searchDrivers: (q: string) => Promise<DriverReferenceSearchResult[]>;
  isLoading: boolean;
}

const ReferenceSelector: React.FC<ReferenceSelectorProps> = ({
  referenceLaps,
  selectedRefType,
  driverRef,
  onSelect,
  onDriverSelect,
  searchDrivers,
  isLoading,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DriverReferenceSearchResult[]>([]);
  const [searchStatus, setSearchStatus] = useState<SearchStatus>('idle');
  const searchAbortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    searchAbortRef.current?.abort();

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearchStatus(trimmed.length === 0 ? 'idle' : 'tooShort');
      return;
    }

    setSearchStatus('searching');
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;
      try {
        const hits = await searchDrivers(trimmed);
        if (controller.signal.aborted) return;
        setResults(hits);
        setSearchStatus(hits.length === 0 ? 'empty' : 'ready');
      } catch (err) {
        if (controller.signal.aborted) return;
        setResults([]);
        setSearchStatus('error');
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      searchAbortRef.current?.abort();
    };
  }, [query, searchDrivers]);

  if (!referenceLaps || selectedRefType === null) return null;

  const activeRef = selectedRefType === 'lapRecord'
    ? referenceLaps.lapRecord
    : selectedRefType === 'personalBest'
      ? referenceLaps.personalBest
      : driverRef;

  if (!activeRef) return null;

  const lr = referenceLaps.lapRecord;
  const pb = referenceLaps.personalBest;
  const sameAsLr = lr && pb && lr.id === pb.id;

  return (
    <div className="ref-selector-wrap" ref={wrapRef}>
      <span className="tele-stat-label">Reference</span>
      <button
        type="button"
        className={`ref-selector-btn${isOpen ? ' open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {isLoading ? '...' : formatMilliseconds(activeRef.time)}
        {!isLoading && activeRef.driverName && (
          <span className="ref-driver">{activeRef.driverName}</span>
        )}
        <span className="ref-chevron">▾</span>
      </button>

      {isOpen && (
        <div className="ref-dropdown">
          <div className="ref-dropdown-section">
            <div className="ref-dropdown-section-label">Lap Record</div>
            {lr ? (
              <div
                className={`ref-option${selectedRefType === 'lapRecord' ? ' active' : ''}`}
                onClick={() => { onSelect('lapRecord'); setIsOpen(false); }}
              >
                <div className="ref-option-left">
                  <div className="ref-option-name">{lr.driverName}</div>
                  <div className="ref-option-meta">LR • {lr.sessionDate}</div>
                </div>
                <div className="ref-option-time">
                  {formatMilliseconds(lr.time)}
                  {selectedRefType === 'lapRecord' && <span className="ref-option-check"> ✓</span>}
                </div>
              </div>
            ) : (
              <div className="ref-option disabled">
                <div className="ref-option-left">
                  <div className="ref-option-name">No lap record</div>
                </div>
              </div>
            )}
          </div>

          <div className="ref-dropdown-section">
            <div className="ref-dropdown-section-label">My Personal Best</div>
            {pb ? (
              <div
                className={`ref-option${selectedRefType === 'personalBest' ? ' active' : ''}`}
                onClick={() => { onSelect('personalBest'); setIsOpen(false); }}
              >
                <div className="ref-option-left">
                  <div className="ref-option-name">
                    Personal Best{sameAsLr ? ' (same as LR)' : ''}
                  </div>
                  <div className="ref-option-meta">Session {pb.sessionDate} • Lap {pb.lap}</div>
                </div>
                <div className="ref-option-time">
                  {formatMilliseconds(pb.time)}
                  {selectedRefType === 'personalBest' && <span className="ref-option-check"> ✓</span>}
                </div>
              </div>
            ) : (
              <div className="ref-option disabled">
                <div className="ref-option-left">
                  <div className="ref-option-name">No personal best</div>
                </div>
              </div>
            )}
          </div>

          <div className="ref-dropdown-section">
            <div className="ref-dropdown-section-label">Another Driver</div>
            <div className="ref-driver-search">
              <input
                type="text"
                className="ref-driver-search-input"
                placeholder="Search driver name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {(searchStatus === 'idle' || searchStatus === 'tooShort') && (
                <div className="ref-driver-search-status">Type at least 2 characters</div>
              )}
              {searchStatus === 'searching' && (
                <div className="ref-driver-search-status">Searching…</div>
              )}
              {searchStatus === 'empty' && (
                <div className="ref-driver-search-status">No drivers found on this track+car</div>
              )}
              {searchStatus === 'error' && (
                <div className="ref-driver-search-status">Couldn't search drivers</div>
              )}
              {searchStatus === 'ready' && (
                <div className="ref-driver-search-results">
                  {results.map((r) => {
                    const hasLastSession = !!(r.lastSessionLap && r.lastSessionLap.lapId);
                    const subRows: Array<{ hit: typeof r.bestLap; kind: 'best' | 'lastSession' }> =
                      hasLastSession
                        ? [
                            { hit: r.bestLap, kind: 'best' },
                            { hit: r.lastSessionLap!, kind: 'lastSession' },
                          ]
                        : [{ hit: r.bestLap, kind: 'best' }];
                    const isCombined = !hasLastSession;
                    return (
                      <div key={r.driverId} className="ref-driver-card">
                        <div className="ref-driver-card-header">{r.driverName}</div>
                        {subRows.map(({ hit, kind }) => {
                          const isActive =
                            selectedRefType === 'driver' && driverRef?.id === hit.lapId;
                          const icon = kind === 'best' ? '★' : '◷';
                          const label = isCombined
                            ? 'Best ever (Last session)'
                            : kind === 'best'
                              ? 'Best ever'
                              : 'Last session';
                          return (
                            <div
                              key={hit.lapId}
                              className={`ref-option ref-driver-card-row${isActive ? ' active' : ''}`}
                              onClick={() => {
                                const ref = referenceLapFromSearch(r.driverId, r.driverName, hit);
                                onDriverSelect(ref);
                                setIsOpen(false);
                              }}
                            >
                              <div className="ref-option-left">
                                <div className="ref-option-name">
                                  <span className="ref-driver-card-row-icon">{icon}</span> {label}
                                </div>
                                <div className="ref-option-meta">{hit.sessionDate}</div>
                              </div>
                              <div className="ref-option-time">
                                {formatMilliseconds(hit.lapTime)}
                                {isActive && <span className="ref-option-check"> ✓</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferenceSelector;
